<script setup>
import {computed, onMounted, watch} from 'vue';
import TeamCard from '@/components/teams/TeamCard.vue';

// Celebration banner for a decided tournament: shown once the knockout Final's
// fixture is played and fully approved by BOTH sides (same rule the feed uses
// for group membership). Fires a confetti burst when the champion appears.
const props = defineProps({
  tournament: {
    type: Object,
    required: true,
  },
});

// StageType.Knockout = 2 (third-place playoff is 3), KnockoutRound.Final = 1.
const finalFixture = computed(() => {
  const finalStage = props.tournament?.stages?.find(s => s.type === 2 && s.round === 1);
  return finalStage?.fixtures?.[0] ?? null;
});

const champion = computed(() => {
  const f = finalFixture.value;
  if (!f?.homeTeam || !f?.awayTeam) return null;
  // Winner exists only for a fully confirmed result: played, both sides
  // approved, and not a draw.
  if (f.homeTeamApprovedAt == null || f.awayTeamApprovedAt == null) return null;
  if (f.homeTeamResult == null || f.awayTeamResult == null) return null;
  const home = Number(f.homeTeamResult);
  const away = Number(f.awayTeamResult);
  if (Number.isNaN(home) || Number.isNaN(away) || home === away) return null;
  return home > away ? f.homeTeam : f.awayTeam;
});

// canvas-confetti touches window/document — client only, dynamic import.
const celebrate = async () => {
  if (import.meta.server) return;
  const confetti = (await import('canvas-confetti')).default;
  const burst = (particleRatio, opts) => confetti({
    particleCount: Math.floor(200 * particleRatio),
    spread: 70,
    origin: {y: 0.35},
    zIndex: 2000,
    ...opts,
  });
  burst(0.5, {angle: 60, origin: {x: 0.1, y: 0.4}});
  burst(0.5, {angle: 120, origin: {x: 0.9, y: 0.4}});
  setTimeout(() => burst(0.35, {spread: 120, startVelocity: 45}), 350);
};

onMounted(() => {
  if (champion.value) celebrate();
});

// Also fires when a live fixtureUpdated refresh decides the winner mid-visit.
watch(champion, (now, before) => {
  if (now && !before) celebrate();
});
</script>

<template>
  <div v-if="champion" class="champion-banner my-4">
    <div class="champion-trophy">
      <v-icon icon="mdi-trophy" size="30"/>
    </div>
    <div class="champion-body">
      <div class="champion-label">Champions</div>
      <team-card :team="champion" size="medium" :show-squash-level="false"/>
    </div>
  </div>
</template>

<style scoped lang="scss">
.champion-banner {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid rgba(240, 180, 41, 0.45);
  background: linear-gradient(115deg, rgba(255, 214, 102, 0.22), rgba(255, 244, 214, 0.5));
}

.champion-trophy {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  border-radius: 50%;
  background: rgba(240, 180, 41, 0.18);
  color: #c98d09;
}

.champion-body {
  min-width: 0;
}

.champion-label {
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #a97807;
  margin-bottom: 4px;
}
</style>
